import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { addDays, formatDay, formatShort, isIsoDate, mealForTime, mealsOf, tiffinsLabel, type Food, type KitchenCustomer, type Meal } from '@tiffinify/core';
import { PageLoader, Badge, BottomNav, BtnSecondary, Callout, Card, DateNav, Empty, FoldCard, FoodMark, LoadError, Hero, Icon, Mono, PageTitle, Section, Segmented, Skel } from '../components/ui';
import { mealCount, mealName } from '../lib/format';
import { KitchenTabs } from '../components/nav';
import { OrdersForMeal } from './Orders';
import { useKitchenDay, useMe, useSettings, useToday } from '../lib/queries';

const FOOD_LABEL: Record<Food, string> = { veg: 'Vegetarian', nonveg: 'Non-vegetarian', egg: 'Egg' };

/** A heading on the packing list: a diet (with only that diet's tiffins) or an area. */
interface Group { key: string; title: string; count: number; food?: Food; customers: KitchenCustomer[] }

export function Kitchen() {
  const today = useToday();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const [picked, setPicked] = useState<Meal | null>((params.get('meal') as Meal | null) ?? null);
  const [groupBy, setGroupBy] = useState<'food' | 'area'>('food');
  const [closed, setClosed] = useState<Set<string>>(new Set());

  const t = today.data;
  const kitchenMeals = t?.meals ?? me.data?.kitchen.meals;
  const served = kitchenMeals ? mealsOf(kitchenMeals) : [];

  // Today is known as soon as the person is: the date navigator is there from the first paint, so nothing moves.
  const todayDate = t?.date ?? me.data?.today;
  const asked = params.get('date');
  const day = asked && isIsoDate(asked) ? asked : params.get('day') === 'next' ? t?.tomorrow.date : todayDate;
  const isToday = !!day && day === todayDate;
  const isNext = !!t && day === t.tomorrow.date;
  const nextWord = t && t.tomorrow.date !== addDays(t.date, 1) ? 'Next' : 'Tomorrow';
  const meal: Meal | undefined = picked && served.includes(picked) ? picked : isToday || !day ? mealForTime(served) : (served[0] ?? 'lunch');

  const k = useKitchenDay(day, meal);
  const d = k.data;
  const dayWord = isToday ? 'Today' : isNext ? nextWord : day ? formatShort(day) : '';

  const onSelectDate = (newDay: string) => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.set('date', newDay);
      n.delete('day');
      return n;
    }, { replace: true });
  };
  // The meal stays in the address, so a refresh or going back keeps it.
  const onSelectMeal = (m: Meal) => {
    setPicked(m);
    setParams((p) => { const n = new URLSearchParams(p); n.set('meal', m); return n; }, { replace: true });
  };
  // Cook and Pack share the day and meal; the view stays in the address too.
  const view = params.get('view') === 'pack' ? 'pack' : 'cook';
  const onSelectView = (v: 'cook' | 'pack') =>
    setParams((p) => { const n = new URLSearchParams(p); if (v === 'pack') n.set('view', 'pack'); else n.delete('view'); return n; }, { replace: true });
  const toggle = (name: string) => setClosed((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });

  // Printing shows once the owner turns it on in Settings (when the kitchen has a printer).
  const printing = !!useSettings().data?.printing;
  const customers = d ? d.sheet.areas.flatMap((a) => a.customers) : [];
  const waitingHere = d ? (t?.pauseRequests ?? []).filter((r) => r.days.includes(d.day) && mealsOf(r.slot).includes(d.meal)) : [];
  const withNotes = customers.filter((c) => c.note);
  const groups: Group[] = !d ? [] : groupBy === 'food'
    ? (['veg', 'nonveg', 'egg'] as const)
        .filter((f) => d.sheet.byFood[f] > 0)
        .map((f) => ({ key: f, title: FOOD_LABEL[f], count: d.sheet.byFood[f], food: f, customers: customers.filter((c) => c.tiffins.some((x) => x.food === f)) }))
    : d.sheet.areas.map((a) => ({ key: a.name, title: a.name, count: a.count, customers: a.customers }));

  return (
    <>
      <main className="main">
        <PageTitle
          title="Kitchen"
          sub={day ? (isToday || isNext ? `${dayWord} · ${formatDay(day)}` : formatDay(day)) : <Skel w={150} />}
        />

        <KitchenTabs value={view} onPick={onSelectView} />

        {day && <DateNav date={day} onChange={onSelectDate} today={todayDate} tomorrow={t?.tomorrow.date} />}

        {served.length > 1 && meal && (
          <Segmented
            label="Meal"
            value={meal}
            onChange={onSelectMeal}
            options={served.map((m) => ({
              value: m,
              label: (
                <span className="seg-tab-content">
                  {mealName(m)}
                  {d && <span className="seg-tab-badge">{d.totals[m]}</span>}
                </span>
              ),
            }))}
          />
        )}

        {k.isError && !d ? (
          <LoadError what="the kitchen list" onRetry={() => void k.refetch()} />
        ) : !meal || !d ? (
          <PageLoader label="Loading the kitchen list" />
        ) : !d.isDeliveryDay ? (
          <Card><Empty title="Kitchen closed">Not a delivery day, or a holiday. Nothing to cook.</Empty></Card>
        ) : (
          <>
            <Hero label={`${dayWord}’s ${meal}`}>
              <Mono muted>{dayWord} · {mealName(meal)}</Mono>
              <div className="number-row">
                <span className="big">{d.sheet.total}</span>
                <span><b>{mealCount(meal, d.sheet.total).split(' ')[1]}</b><small>to cook for {formatDay(d.day)}</small></span>
              </div>
              {d.sheet.total > 0 && (
                <div className="food-facts">
                  <span><FoodMark food="nonveg" plate />{d.sheet.byFood.nonveg} non-veg</span>
                  <span><FoodMark food="veg" plate />{d.sheet.byFood.veg} veg</span>
                  {d.sheet.byFood.egg > 0 && <span><FoodMark food="egg" plate />{d.sheet.byFood.egg} egg</span>}
                </div>
              )}
            </Hero>

            {waitingHere.length > 0 && (
              <Callout icon="pause" title={`${waitingHere.length} pause ${waitingHere.length === 1 ? 'request' : 'requests'} waiting for this day`}>
                {waitingHere.map((r) => r.name).join(', ')} asked to skip it. Answer on <Link to="/">Today</Link> before you cook.
              </Callout>
            )}

            {view === 'cook' && d.sheet.total === 0 && (
              <Card><Empty title="No one to cook for">No one has {meal} on this day.</Empty></Card>
            )}

            {view === 'cook' && d.sheet.byDish.length > 0 && (
              <Section title="Dishes to cook" meta={<Badge kind="saffron">{d.sheet.byDish.length} {d.sheet.byDish.length === 1 ? 'dish' : 'dishes'}</Badge>}>
                <div className="kitchen-dish-stack">
                  {d.sheet.byDish.map((x) => (
                    <div key={`${x.food}:${x.name}`} className="kitchen-dish-ticket">
                      <div className="kitchen-dish-info">
                        <FoodMark food={x.food} />
                        <div className="kitchen-dish-meta">
                          <span className="kitchen-dish-name">{x.name}</span>
                          <span className={`kitchen-dish-kind${x.custom ? ' chosen' : ''}`}>{x.pool ? 'Dish pool · rotates daily' : x.custom ? x.category ?? 'Fixed dish' : 'Chef’s special'}</span>
                        </div>
                      </div>
                      <span className="kitchen-dish-qty">×{x.count}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {view === 'cook' && withNotes.length > 0 && (
              <Section title="Notes for the cook" meta={<Badge kind="due">{withNotes.length}</Badge>}>
                <Card className="chef-notes-card">
                  {withNotes.map((c) => <CookSheetRow key={c.subscriptionId} c={c} />)}
                </Card>
              </Section>
            )}

            {view === 'pack' && (d.sheet.total === 0 ? (
              <Card><Empty title="No one to cook for">No one has {meal} on this day.</Empty></Card>
            ) : (
              <Section title="Packing list" meta={<Badge>{d.sheet.total} {d.sheet.total === 1 ? 'tiffin' : 'tiffins'}</Badge>}>
                <Segmented
                  label="Group the packing list"
                  value={groupBy}
                  onChange={setGroupBy}
                  options={[{ value: 'food', label: 'By diet' }, { value: 'area', label: 'By area' }]}
                />
                <div className="stack cards">
                  {groups.map((g) => (
                    <FoldCard
                      key={g.key}
                      icon={g.food ? <FoodMark food={g.food} /> : <Icon name="pin" size={16} />}
                      title={g.title}
                      meta={<span className="group-head-badge">{mealCount(meal, g.count)}</span>}
                      open={!closed.has(g.key)}
                      onToggle={() => toggle(g.key)}
                    >
                      {g.customers.map((c) => <CookSheetRow key={c.subscriptionId} c={c} food={g.food} hideArea={!g.food} />)}
                    </FoldCard>
                  ))}
                </div>
                {printing && <BtnSecondary label="Print labels and delivery list" icon="print" to={`/print?date=${d.day}&meal=${d.meal}`} />}
              </Section>
            ))}
            <OrdersForMeal day={d.day} meal={d.meal} mode={view} />
          </>
        )}
      </main>
      <BottomNav />
    </>
  );
}

/** One customer on the kitchen list. Under a diet heading, only their tiffins in that diet. */
function CookSheetRow({ c, food, hideArea }: { c: KitchenCustomer; food?: Food; hideArea?: boolean }) {
  const tiffins = food ? c.tiffins.filter((x) => x.food === food) : c.tiffins;
  const portions = tiffins.reduce((n, x) => n + x.quantity, 0);
  const label = tiffinsLabel(tiffins);
  const foods = [...new Set(tiffins.map((x) => x.food))];
  return (
    <div className="eat">
      <span className="row eat-marks">{foods.map((f) => <FoodMark key={f} food={f} />)}</span>
      <span className="grow">
        <span className="mark-name">
          <b>{c.name}</b>
          {portions > 1 && <Badge>×{portions}</Badge>}
          {label && <Badge kind={tiffins.some((x) => x.custom) ? 'saffron' : undefined}>{label}</Badge>}
          {c.extra && <Badge kind="saffron">+{c.extra} extra</Badge>}
          {c.lastDay && <Badge kind="end">Last day</Badge>}
          {c.movedFrom && c.movedFrom.length > 0 && <Badge>Moved</Badge>}
        </span>
        {c.note && <span className="cook-chef-note"><Icon name="sparkle" size={12} />{c.note}</span>}
        {!hideArea && c.area && <small className="cook-area-tag"><Icon name="pin" size={12} /> {c.area}</small>}
      </span>
    </div>
  );
}
